import { ref, computed, watch, toValue } from "vue"
import { format } from "date-fns"
import { nl } from "date-fns/locale"
import { EM_DASH } from "@/helpers/energyBillFormat.js"

/**
 * Day-level navigation within an externally-provided list of available days
 * (sorted ISO-`yyyy-MM-dd` strings). When the user-selected day is not in the
 * list (or none is selected), `effectiveDayKey` falls back to the first day.
 *
 * @param {import('vue').MaybeRefOrGetter<string[]>} availableDays
 * @param {import('vue').WatchSource} [resetOn]  source that, when changed, clears the user's day selection
 */
export default function useDayNavigation(availableDays, resetOn) {
  const selectedDayKey = ref(null)

  const days = computed(() => toValue(availableDays))

  const effectiveDayKey = computed(() => {
    const list = days.value
    if (!list.length) {
      return null
    }
    if (selectedDayKey.value && list.includes(selectedDayKey.value)) {
      return selectedDayKey.value
    }
    return list[0]
  })

  const dayLabel = computed(() => {
    const key = effectiveDayKey.value
    if (!key) {
      return EM_DASH
    }
    // Matches the date format used by the toolbar DatePicker (e.g. "1 jun. 2026")
    return format(new Date(`${key}T00:00:00`), "d MMM yyyy", { locale: nl })
  })

  const currentIndex = computed(() => days.value.indexOf(effectiveDayKey.value))

  const canGoPrevDay = computed(() => currentIndex.value > 0)
  const canGoNextDay = computed(
    () => currentIndex.value >= 0 && currentIndex.value < days.value.length - 1,
  )

  function prevDay() {
    if (!canGoPrevDay.value) {
      return
    }
    selectedDayKey.value = days.value[currentIndex.value - 1]
  }

  function nextDay() {
    if (!canGoNextDay.value) {
      return
    }
    selectedDayKey.value = days.value[currentIndex.value + 1]
  }

  if (resetOn) {
    watch(resetOn, () => {
      selectedDayKey.value = null
    })
  }

  return {
    selectedDayKey,
    effectiveDayKey,
    dayLabel,
    canGoPrevDay,
    canGoNextDay,
    prevDay,
    nextDay,
  }
}
